/*
===============================================================================================================
                                 Copyright 2022-2024, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useEffect, useContext, useRef, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from '@/contexts/theme-context';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import ClickAway from '@/components/click-away';
import { LocaleContext } from '@/contexts/locale-context';
import TextDiv from '@/components/text-div';
import { toWebSafeFormat } from '@/utilities/strings';
import Tick from '@/public/icons/primary-tick.svg';
import TooltipIcon from '@/public/icons/tooltip.svg';
import Tooltip from '@/components/tooltip';
import FileUploader from '@/components/file-uploader';
import SimpleButton from '@/components/button-simple';

/**
 * TextInput contains an input field, header, error message, and tooltip.
 * Is wrapped in the ErrorReporting HOC.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} title - Displayed above the text field
 * @param {string} smallTitle - Displayed above the text field, when the screen is small
 * @param {boolean} titleWithMarginBottom - controls if title has margin bottom Defaults to null
 * @param {Array} defaultValue - array of strings
 * @param {number} inputWidth - CSS Percentage width. Defaults to 100
 * @param {boolean} noDropdownListGap - Whether or not dropdown list has a gap between itself and dropdown-selection, when shown
 * @param {boolean} withFlexTitle - Whether or not title text should use flexbox to expand its height
 * @param {boolean} required - Whether or not this is a required field
 * @param {string} placeholder - Placeholder text
 * @param {boolean} tooltip - Optional - select item hover tooltip
 * @param {function} onChange - Optional function called when the input value changes
 * @param {Object} validationRules - Full description in error-reporting.jsx
 * @param {string} error - Optional. Display this error on component load
 */
const MultiSelect = forwardRef(({
  inputSelectorId,
  title,
  smallTitle,
  titleWithMarginBottom,
  defaultValue,
  inputWidth,
  required,
  placeholder,
  noDropdownListGap,
  withFlexTitle,
  tooltip,
  withFileImport,
  fileImportTooltip,
  onChange,
  setContent, // Passed in automatically, no need to set
  error, // Passed in automatically, no need to set
  errorTextDirection, // Passed in automatically, no need to set it
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const { textColor, alternateTextColor } = useContext(ThemeContext);
  const { textDirection, translate } = useContext(LocaleContext);
  const [items, setItems] = useState(null);
  const [fileError, setFileError] = useState(null);
  const inputRef = useRef(null);

  const defaultFileImportTooltip = (
    <>
      <strong>{translate('rules.csvTooltip.heading')}</strong>
      <br />
      <span>{translate('rules.csvTooltip.text')}</span>
    </>
  );

  useEffect(() => {
    if (items) return;
    const newItems = defaultValue.map((item) => ({ value: item, selected: true }));
    setItems(newItems);
  }, [defaultValue]);

  useEffect(() => {
    setFileError(null);

    if (!items) return;

    const itemsContent = items
      .filter((item) => item.selected)
      .map((item) => item.value);

    if (JSON.stringify(itemsContent) !== JSON.stringify(defaultValue)) {
      if (onChange) {
        onChange(itemsContent);
        setContent(itemsContent);
      }
    }
  }, [items]);

  const onSelectionChange = (index) => {
    const newItems = [...items];
    newItems[index] = ({ value: newItems[index].value, selected: !newItems[index].selected });
    setItems(newItems);
  };

  const onKeyDown = (event, index, func) => {
    if (event.key === 'Enter') {
      func(index);
    }
  };

  const addItem = (value) => {
    if (items.map((item) => item.value).includes(value)) return;

    const newItems = [...items];
    newItems.push({ value, selected: true });
    setItems(newItems);
  };

  const onTextInputKeydown = (event) => {
    const { value } = inputRef.current;
    if (event.key === 'Enter' && value) {
      addItem(value);
      inputRef.current.value = '';
    }
  };

  const onTextInputBlur = () => {
    const { value } = inputRef.current;
    if (value) {
      addItem(value);
      inputRef.current.value = '';
    }
  };

  const onFileUpload = (values) => {
    if (values) {
      setItems(values.map((val) => ({ value: val, selected: true })));
    }
  };

  const getTruncatedItemsList = () => {
    const maxItemsShown = 20;
    const selectedItems = items?.filter((item) => item.selected);
    if (selectedItems?.length > maxItemsShown) {
      return [...selectedItems.slice(0, maxItemsShown), { value: `+${selectedItems.length - maxItemsShown} ${translate('constants.more')}` }];
    }
    return selectedItems;
  };

  const selectAll = () => {
    const newItems = items.map(({ value }) => ({ value, selected: true }));
    setItems(newItems);
  };

  const deselectAll = () => {
    const newItems = items.map(({ value }) => ({ value, selected: false }));
    setItems(newItems);
  };

  const errorText = fileError || error;
  return (
    <div id={inputSelectorId} className="multiselect">
      {title && (
        <div className={`${smallTitle ? 'small-title' : 'title'} ${withFlexTitle ? 'flex' : ''}`}>
          <TextDiv>
            {`${title}${required === true ? '*' : ''}`}
          </TextDiv>
        </div>
      )}
      <ClickAway shouldWeListen={isOpen} onClickAway={() => { setIsOpen(false); }}>
        <div className={`input-wrapper${errorText ? ' input-error' : ''}`}>
          <input
            id={`${inputSelectorId}-input`}
            ref={inputRef}
            type="text"
            dir={textDirection}
            placeholder={placeholder}
            onKeyDown={(event) => onTextInputKeydown(event)}
            onBlur={(event) => onTextInputBlur(event)}
            onFocus={() => setIsOpen(true)}
          />
          {getTruncatedItemsList()?.length ? (
            <div className="multiselect-selection">
              {getTruncatedItemsList().map((item) => <span key={item.value}>{item.value}</span>)}
            </div>
          ) : null}
        </div>
        {withFileImport && (
          <div className="import-file-link">
            <FileUploader
              inputSelectorId={`${inputSelectorId}-file-upload`}
              onChange={onFileUpload}
              onError={setFileError}
              buttonText={translate('rules.fileUploadLink')}
              isClearable={false}
              showFilename={false}
              fileType="csv"
              showAsLink
            />
            <Tooltip content={fileImportTooltip ?? defaultFileImportTooltip} triggerOnClick>
              <div
                className="tooltip-button-wrapper"
                aria-label={translate('constants.tooltipLabels.questionMark')}
                role="button"
                tabIndex={0}
              >
                <TooltipIcon />
              </div>
            </Tooltip>
          </div>
        )}

        <div className={`error ${errorText ? ' visible' : ''} ${errorTextDirection}`} ref={ref}>
          {errorText && <TextDiv>{errorText}</TextDiv>}
        </div>
        <div
          id={`${inputSelectorId}-multiselect-list`}
          className={`multiselect-list${isOpen && items.length ? ' open' : ''}${noDropdownListGap ? ' no-gap' : ''}`}
        >
          <div className="select-all-controls">
            <SimpleButton
              inputHeight={32}
              inputWidth={140}
              hasMargin={false}
              onClick={selectAll}
              isLozenge
            >
              {translate('rules.selectAll')}
            </SimpleButton>
            <SimpleButton
              inputHeight={32}
              inputWidth={140}
              hasMargin={false}
              onClick={deselectAll}
              isLozenge
            >
              {translate('rules.deselectAll')}
            </SimpleButton>
          </div>
          {items?.map((item, index) => (
            <div
              title={tooltip ? `${item.title} (${item.value}) ` : ''}
              className="multiselect-item"
              key={item.value}
              id={`multiselect-item-${toWebSafeFormat(item.value)}`}
              dir={textDirection}
              onClick={() => onSelectionChange(index)}
              onKeyDown={(event) => onKeyDown(event, index, onSelectionChange)}
              role="button"
              tabIndex={0}
            >
              <div className="item-value">{item.value}</div>
              <div className="item-selected">{item.selected && <Tick />}</div>
            </div>
          ))}
        </div>
      </ClickAway>

      <style jsx>
        {`
          .multiselect {
            color: ${textColor};
            cursor: pointer;
            display: flex;
            flex-direction: column;
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-body);
            position: relative;
            width: ${inputWidth}%;
          }

          .title {
            color: var(--color-primary-dark);
            display: block;
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H2);
            line-height: 2;
          }

          .small-title {
            color: var( --color-title-grey);
            display: block;
            font-size: var(--font-size-address);
            margin-bottom: ${titleWithMarginBottom ? '0' : '12'}px;
            text-overflow: ellipsis;
          }

          .title.flex {
            display: flex;
            flex-grow: 1;
          }

          .input-wrapper {
            position: relative;
            border: 1px solid var(--color-mid-grey);
            border-radius: var(--border-radius);
          }

          input {
            border-radius: var(--border-radius);
            border: transparent;
            color: var(--color-primary-dark);
            font-size: var(--font-size-body);
            font-weight: var(--font-weight-body);
            height: 48px;
            padding: var(--spacing-small);
            width: 100%;
          }

          input::placeholder { 
            color: var(--color-mid-grey);
            font-size: var(--font-size-link);
          }

          input:disabled { 
            color: var(--color-dark-grey);
            background-color: var(--color-light-grey);
          }

          .input-error {
            border: 1px solid var(--color-error-red);
          }

          .import-file-link {
            position: absolute;
            top: ${smallTitle ? '-4px' : '-24px'};
            right: 3px;
            display: flex;
            align-items: center;
            column-gap: 3px;
            font-size: var(--font-size-H4);
          }

          .item-selected {
            height: 24px;
            transform: scale(0.25);
            margin-top: -6px;
          }

          .item-value {
            flex-grow: 1;
          }

          .multiselect-selection {
            background-color: var(--color-white);
            font-size: var(--font-size-H4);
            font-weight: var(--font-weight-body);
            display: flex;
            flex-wrap: wrap;
            column-gap: 5px;
            row-gap: 5px;
            padding: 10px 14px 5px;
          }

          .multiselect-selection span {
            background: var(--color-light-grey);
            border-radius: var(--border-radius-small);
            display: flex;
            height: 24px;
            line-height: 22px;
            max-width: 100%;
            padding: 0 6px;
            user-select: none;
          }

          .multiselect-list { 
            animation: fadeIn 0.2s ease-in-out;
            align-items: left;
            background-color: var(--color-white);
            border-radius: var(--border-radius);
            border: 1px solid var(--color-line-grey);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
            display: none;
            flex-direction: column;
            margin-top: -17px;
            max-height: 240px;
            overflow-y: auto;
            position: absolute;
            scroll-behavior: smooth;
            scrollbar-width: thin;
            scrollbar-color: var(--color-mid-grey)  var(--color-white);
            width: 100%;
            z-index: var(--zindex-dropdown);
          }

          .multiselect-list.no-gap {
            margin-top: -22px;
            border-radius: 0 0 3px 3px;
          }

          .multiselect-list.open {
            display: block;
          }

          .select-all-controls {
            border-bottom: 1px solid var(--color-line-grey);
            display: flex;
            justify-content: space-around;
            padding: 5px 0;
          }

          .multiselect-item {
            align-items: center;
            border-bottom: 1px solid var(--color-line-grey);
            display: flex;
            line-height: 24px;
            min-height: 48px;
            overflow: hidden;
            padding: 12px 30px 12px 15px;
            word-break: break-word;
          }

          .multiselect-item.selected {
            background-color: var(--color-line-grey);
          }

          .multiselect-item:hover {
            background-color: var(--color-hover-dark-blue);
            color: ${alternateTextColor};
          }

          .multiselect-item:last-child {
            border-bottom: none;
          }

          .error {
            margin-top: 3px;
            min-height: 20px;
            visibility: hidden;
            white-space: break-spaces;
          }

          .error.visible {
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            margin-top: 3px;
            visibility: visible;
          }

          .error.visible + .dropdown-list.open {
            margin-top: -18px;
          }

          .error.rtl {
            text-align: right;
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(-30px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media only screen and (max-width: 768px) {
            .multiselect {
              font-size: var(--font-size-body-mobile);
              font-weight: var(--font-weight-body-mobile);
              margin-top: 15px;
              width: 100%;
            }

            .title {
              font-size: var(--font-size-H2-mobile);
              font-weight: var(--font-weight-H2);
            }

            .error.visible {
              font-size: var(--font-size-error-mobile);
            }
          }
        `}
      </style>
    </div>
  );
});

MultiSelect.defaultProps = {
  title: null,
  required: false,
  defaultValue: [],
  onChange: null,
  inputSelectorId: null,
  error: null,
  placeholder: '',
  inputWidth: 100,
  noDropdownListGap: false,
  withFlexTitle: false,
  tooltip: false,
  withFileImport: false,
  fileImportTooltip: null,
  errorTextDirection: 'ltr',
  smallTitle: false,
  titleWithMarginBottom: null,
};

MultiSelect.propTypes = {
  title: PropTypes.string,
  required: PropTypes.bool,
  defaultValue: PropTypes.arrayOf(PropTypes.string.isRequired),
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  inputSelectorId: PropTypes.string,
  setContent: PropTypes.func.isRequired,
  error: PropTypes.string,
  inputWidth: PropTypes.number,
  noDropdownListGap: PropTypes.bool,
  withFlexTitle: PropTypes.bool,
  tooltip: PropTypes.bool,
  withFileImport: PropTypes.bool,
  fileImportTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  errorTextDirection: PropTypes.string,
  smallTitle: PropTypes.bool,
  titleWithMarginBottom: PropTypes.bool,
};

export default ErrorReportingHOC(MultiSelect);
